import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { CalendarIcon, Check, ChevronsUpDown, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { daysBetween, toISODate } from "@/lib/utils2";

const EMPTY_FORM = {
  employee_id: "", first_name: "", last_name: "", email: "",
  department: "", site: "", job_role: "",
  supervisor_id: "",
  start_date: undefined, end_date: undefined,
  request_type: "Absence",
  notes: "",
  status: "approved",
  send_emails: false,
};

export default function AdminAddRequestDialog({ open, onOpenChange, reference, onCreated, prefill = null }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [employees, setEmployees] = useState([]);
  const [empOpen, setEmpOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Apply prefill (e.g. clicked from Team Calendar) over the empty form.
    const base = { ...EMPTY_FORM };
    if (prefill) {
      if (prefill.employee) {
        const e = prefill.employee;
        const sup = reference?.supervisors?.find((s) => s.id === e.supervisor_id);
        Object.assign(base, {
          employee_id: e.id || "",
          first_name: e.first_name || "",
          last_name: e.last_name || "",
          email: e.email || "",
          department: e.department || "",
          site: e.site || "",
          job_role: (e.job_roles && e.job_roles[0]) || "",
          supervisor_id: sup ? sup.id : "",
        });
      }
      if (prefill.date) {
        base.start_date = prefill.date;
        base.end_date = prefill.date;
      }
    }
    setForm(base);
    api.get("/employees").then(({ data }) => setEmployees(data));
  }, [open, prefill]); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const days = useMemo(
    () => daysBetween(toISODate(form.start_date), toISODate(form.end_date)),
    [form.start_date, form.end_date]
  );

  const pickEmployee = (emp) => {
    const sup = reference?.supervisors?.find((s) => s.id === emp.supervisor_id);
    setForm((f) => ({
      ...f,
      employee_id: emp.id,
      first_name: emp.first_name,
      last_name: emp.last_name,
      email: emp.email || f.email,
      department: emp.department,
      site: emp.site || "",
      job_role: (emp.job_roles && emp.job_roles[0]) || f.job_role,
      supervisor_id: sup ? sup.id : f.supervisor_id,
    }));
    setEmpOpen(false);
  };

  const supervisorsForDept = useMemo(() => {
    if (!reference || !form.department) return reference?.supervisors || [];
    const matches = reference.supervisors.filter((s) => !s.is_admin && s.departments.includes(form.department));
    const admin = reference.supervisors.find((s) => s.is_admin);
    return admin ? [...matches, admin] : matches;
  }, [reference, form.department]);

  const submit = async (overrideClash = false) => {
    const required = ["first_name", "last_name", "email", "department", "job_role", "supervisor_id"];
    const missing = required.filter((k) => !form[k]);
    if (missing.length) {
      toast.error("Please fill required field" + (missing.length > 1 ? "s" : "") + ": " + missing.map((k) => k.replace("_", " ")).join(", "));
      return;
    }
    if (!form.start_date || !form.end_date) { toast.error("Pick start and end dates"); return; }
    if (days <= 0) { toast.error("End date must be on or after start date"); return; }

    setSubmitting(true);
    try {
      const payload = {
        employee_id: form.employee_id || null,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        department: form.department,
        site: form.site || null,
        job_role: form.job_role,
        supervisor_id: form.supervisor_id,
        start_date: toISODate(form.start_date),
        end_date: toISODate(form.end_date),
        days,
        request_type: form.request_type || "Absence",
        notes: form.notes || null,
        status: form.status,
        send_emails: !!form.send_emails,
        override_clash: !!overrideClash,
      };
      const { data } = await api.post("/admin/holiday-requests", payload);
      toast.success(`Absence notification created (${data.status})`);
      onOpenChange(false);
      onCreated?.(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "clash_hard_block") {
        const list = (detail.clashes || []).slice(0, 3)
          .map((c) => `${c.start_date} → ${c.end_date} (${c.status})`).join("\n");
        const ok = window.confirm(
          `Clash detected — this contractor already has an approved/pending overlap:\n\n${list}\n\nApprove and create anyway?`
        );
        if (ok) {
          setSubmitting(false);
          return submit(true);
        }
        toast.message("Creation cancelled — clash not overridden.");
      } else {
        let msg;
        if (Array.isArray(detail)) {
          msg = detail.map((d) => (typeof d === "string" ? d : (d?.msg || JSON.stringify(d)))).join("; ");
        } else if (typeof detail === "string") {
          msg = detail;
        } else {
          msg = e?.message || "Failed to create";
        }
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const empLabel = form.first_name && form.last_name
    ? `${form.first_name} ${form.last_name}${form.department ? ` · ${form.department}` : ""}`
    : "Search contractors…";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="admin-add-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Plus className="w-5 h-5 text-primary"/> Add absence notification
          </DialogTitle>
          <DialogDescription>
            Manually create an absence notification. No department code required. Choose whether to notify the contractor/supervisor by email.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Contractor combobox */}
          <Field label="Contractor (optional — pick to autofill)">
            <Popover open={empOpen} onOpenChange={setEmpOpen}>
              <PopoverTrigger asChild>
                <Button
                  data-testid="add-req-employee-btn"
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between font-normal h-10"
                >
                  <span className="truncate">{empLabel}</span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50"/>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                <Command>
                  <CommandInput data-testid="add-req-employee-search" placeholder="Search by name…"/>
                  <CommandList>
                    <CommandEmpty>No contractor found.</CommandEmpty>
                    <CommandGroup>
                      {employees.slice(0, 200).map((e) => (
                        <CommandItem
                          key={e.id}
                          value={`${e.first_name} ${e.last_name} ${e.department}`}
                          onSelect={() => pickEmployee(e)}
                          data-testid={`add-req-emp-${e.id}`}
                        >
                          <Check className={cn("mr-2 h-4 w-4", form.employee_id === e.id ? "opacity-100" : "opacity-0")}/>
                          <div className="flex-1 min-w-0">
                            <div className="truncate">{e.first_name} {e.last_name}</div>
                            <div className="text-xs text-muted-foreground truncate">{e.department}{e.job_roles?.length ? ` · ${e.job_roles.join(", ")}` : ""}</div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="First name *"><Input data-testid="add-req-first" value={form.first_name} onChange={(e) => set("first_name", e.target.value)}/></Field>
            <Field label="Last name *"><Input data-testid="add-req-last" value={form.last_name} onChange={(e) => set("last_name", e.target.value)}/></Field>
          </div>
          <Field label="Email *">
            <Input data-testid="add-req-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)}/>
            {form.employee_id && !form.email && (
              <p className="text-xs text-amber-700 mt-1" data-testid="add-req-email-hint">
                This contractor has no email on record — please type one before saving.
              </p>
            )}
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Department *">
              <Select value={form.department} onValueChange={(v) => set("department", v)}>
                <SelectTrigger data-testid="add-req-dept"><SelectValue placeholder="Select"/></SelectTrigger>
                <SelectContent>{reference?.departments?.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Site (optional)">
              <Select value={form.site || "__none"} onValueChange={(v) => set("site", v === "__none" ? "" : v)}>
                <SelectTrigger data-testid="add-req-site"><SelectValue placeholder="None"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">None</SelectItem>
                  {reference?.sites?.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Job role *">
              <Select value={form.job_role} onValueChange={(v) => set("job_role", v)}>
                <SelectTrigger data-testid="add-req-role"><SelectValue placeholder="Select"/></SelectTrigger>
                <SelectContent>{reference?.job_roles?.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Supervisor *">
              <Select value={form.supervisor_id} onValueChange={(v) => set("supervisor_id", v)}>
                <SelectTrigger data-testid="add-req-supervisor"><SelectValue placeholder="Select"/></SelectTrigger>
                <SelectContent>
                  {supervisorsForDept.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}{s.is_admin ? " — Main Manager" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Request type">
              <Select value={form.request_type} onValueChange={(v) => set("request_type", v)}>
                <SelectTrigger data-testid="add-req-type"><SelectValue/></SelectTrigger>
                <SelectContent>{(reference?.request_types || ["Absence"]).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="add-req-status"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {["pending","approved","rejected","cancelled"].map((s) => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date *"><DatePickerInput testid="add-req-start" value={form.start_date} onChange={(d) => set("start_date", d)}/></Field>
            <Field label="End date *"><DatePickerInput testid="add-req-end" value={form.end_date} onChange={(d) => set("end_date", d)} fromDate={form.start_date}/></Field>
          </div>

          <div className="bg-accent/40 border border-border rounded-lg px-3 py-2 flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Number of days</span>
            <span className="font-heading text-xl font-semibold tabular-nums" data-testid="add-req-days">{days}</span>
          </div>

          <Field label="Notes / reason (optional)">
            <Textarea data-testid="add-req-notes" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)}/>
          </Field>

          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <Checkbox data-testid="add-req-send-emails" checked={form.send_emails} onCheckedChange={(c) => set("send_emails", !!c)}/>
            <span>Send notification email{form.status === "pending" ? "s to contractor and supervisor" : " to the contractor"}</span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="add-req-cancel">Cancel</Button>
          <Button data-testid="add-req-save" onClick={() => submit(false)} disabled={submitting} className="gap-1.5">
            {submitting ? "Saving…" : (<><Plus className="w-4 h-4"/> Create request</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground font-semibold">{label}</Label>
      {children}
    </div>
  );
}

function DatePickerInput({ value, onChange, testid, fromDate }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button data-testid={testid} variant="outline" className="w-full justify-start text-left font-normal h-10">
          <CalendarIcon className="mr-2 h-4 w-4"/>
          {value ? format(value, "PPP") : <span className="text-muted-foreground">Pick a date</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={value} onSelect={onChange} fromDate={fromDate} initialFocus/>
      </PopoverContent>
    </Popover>
  );
}
